import React, { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Box, Button, Chip, CircularProgress, Typography } from '@mui/material';
import { AttachFile as AttachFileIcon, Download as DownloadIcon } from '@mui/icons-material';
import { vouchersApi } from '../../services/expenseVouchersApi';

const formatSize = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Bills of one expense line. Read-only unless onUpload / onDelete are supplied.
//   rules: { allowedExtensions, maxFileSizeBytes, maxBillsPerLine } from form-context
//   onDownload(bill): override the download (e.g. the approval endpoint)
const BillList = ({ voucherId, bills = [], rules, onUpload, onDelete, onDownload, disabled, error }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const editable = Boolean(onUpload);

  const download = async (bill) => {
    try {
      await (onDownload ? onDownload(bill) : vouchersApi.downloadBill(voucherId, bill));
    } catch (e) {
      toast.error(e.message);
    }
  };

  // Client checks give quick feedback; the server re-validates every file.
  const pick = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;

    if (rules) {
      const allowed = rules.allowedExtensions || [];
      const bad = files.find((f) => !allowed.includes(`.${f.name.split('.').pop().toLowerCase()}`));
      if (bad) return toast.error(`"${bad.name}" is not an allowed file type (${allowed.join(', ')}).`);
      const big = files.find((f) => f.size > rules.maxFileSizeBytes);
      if (big) return toast.error(`"${big.name}" is larger than ${formatSize(rules.maxFileSizeBytes)}.`);
      if (bills.length + files.length > rules.maxBillsPerLine) {
        return toast.error(`An expense line can have at most ${rules.maxBillsPerLine} bills.`);
      }
    }

    setBusy(true);
    try {
      await onUpload(files);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        {bills.map((bill) => (
          <Chip
            key={bill._id}
            icon={<DownloadIcon />}
            label={`${bill.originalName} (${formatSize(bill.size)})`}
            onClick={() => download(bill)}
            onDelete={editable && onDelete && !disabled ? () => onDelete(bill) : undefined}
            variant="outlined"
            size="small"
            title="Download"
          />
        ))}
        {!bills.length && !editable && <Typography variant="body2" color="error">Bill not provided</Typography>}
        {editable && (
          <>
            <input
              ref={inputRef}
              type="file"
              multiple
              hidden
              data-testid="bill-input"
              accept={(rules?.allowedExtensions || []).join(',')}
              onChange={pick}
            />
            <Button
              size="small"
              variant="text"
              startIcon={busy ? <CircularProgress size={14} /> : <AttachFileIcon />}
              onClick={() => inputRef.current?.click()}
              disabled={disabled || busy}
              sx={{ textTransform: 'none' }}
            >
              {busy ? 'Uploading...' : 'Attach bill'}
            </Button>
          </>
        )}
      </Box>
      {error && <Typography variant="caption" color="error">{error}</Typography>}
    </Box>
  );
};

export default BillList;
